import { useMemo } from 'react';
import { RadixSelect } from '@/components/ui';
import { useActivePoliciesByType } from '../hooks/usePoliciesQuery';
import type { PolicyType } from '../types/policy';

export interface PolicySelectProps {
  /** Restricts options to active policies of this type. Omit for all types. */
  type?: PolicyType;
  /** Selected policy id, or null when nothing is chosen. */
  value: number | null;
  onChange: (policyId: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const UNSET = '';

/**
 * Active-policy dropdown for a given PolicyType, backed by useActivePoliciesByType.
 *
 * Standalone component — intentionally NOT wired into ProductForm here
 * (that integration is tracked separately under BUG-007 / Phase 3).
 */
export const PolicySelect = ({
  type,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  ariaLabel,
}: PolicySelectProps) => {
  const query = useActivePoliciesByType(type);

  const options = useMemo(() => {
    const items = query.data?.items ?? [];
    return [
      { value: UNSET, label: placeholder ?? '정책 선택' },
      ...items.map((policy) => ({ value: String(policy.id), label: policy.name })),
    ];
  }, [query.data, placeholder]);

  return (
    <RadixSelect
      value={value != null ? String(value) : UNSET}
      onChange={(v) => onChange(v ? Number(v) : null)}
      options={options}
      placeholder={placeholder ?? '정책 선택'}
      disabled={disabled || query.isPending}
      {...(className !== undefined ? { className } : {})}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
    />
  );
};
